import {DataStorageService} from "src/app/infrastructure/data-storage.service";
import {effect, inject, Injectable} from "@angular/core";
import {Samples} from "src/app/infrastructure/moovement-api/models/samples";
import {FarmApiClient} from "src/app/infrastructure/moovement-api/web-api/farm.api-client";
import {AssetSampleApiClient} from "src/app/infrastructure/moovement-api/web-api/asset-sample.api-client";
import {WaterLevelService} from "src/app/domain/services/water-level.service";
import {DataSubscriptionService} from "src/app/infrastructure/data-subscription.service";
import {forkJoin, tap} from "rxjs";
import {FarmReference} from "src/app/infrastructure/moovement-api/models/farm";
import {
  Asset,
  FixedSamplePoint,
  LoRaGateway,
  LoRaGatewayStatus,
  WaterPoint
} from "src/app/infrastructure/moovement-api/models/asset";
import {descendingAssetSamples, getValueByKeyMatching, hasMatchingKey, sinceOneHour,} from "src/app/app.config";
import {UserLocationStore} from "src/app/domain/user/user-location.store";
import {AnimalsStore} from "src/app/domain/assets/animals/animals.store";
import {MapStore} from "src/app/domain/map/map.store";
import {FarmStore} from "src/app/domain/assets/farm/farm.store";
import {DeviceStore} from "src/app/domain/devices/device.store";
import {DeviceAssociationStore} from "src/app/domain/devices/device-association.store";
import {GatewayStore} from "src/app/domain/devices/gateway/gateway.store";
import {GatewayStatusStore} from "src/app/domain/devices/gateway/gateway-status.store";
import {GatewayService} from "src/app/domain/devices/gateway/gateway.service";
import {ProximitySampleStore} from "src/app/domain/samples/proximity-samples/proximity-samples.store";
import {AggregatedAssetStateService} from "src/app/domain/assets/aggregated-state/aggregated-asset-state.service";
import {AggregatedDeviceStateService} from "src/app/domain/devices/aggregated-state/aggregated-device-state.service";
import {RssiStore} from "src/app/domain/samples/rssi/rssi.store";
import {RssiService} from "src/app/domain/samples/rssi/rssi.service";
import {BatteryVoltage} from "src/app/domain/samples/rssi/battery-voltage";
import {FarmService} from "src/app/domain/assets/farm/farm.service";
import {PaddockService} from "src/app/domain/assets/paddocks/paddock.service";
import {FixedSamplePointsStore} from "src/app/domain/assets/fixed-sample-points/fixed-sample-points.store";
import {FixedSamplePointsService} from "src/app/domain/assets/fixed-sample-points/fixed-sample-points.service";
import {AnimalsService} from "src/app/domain/assets/animals/animals.service";
import {NotificationsService} from "src/app/presentation/main-view/notifications/notification.service";
import {WaterPointsStore} from "src/app/domain/assets/water-points/water-points.store";
import {WaterPointsService} from "src/app/domain/assets/water-points/water-points.service";
import {DeviceService} from "src/app/domain/devices/device.service";
import {DeviceAssociationService} from "src/app/domain/devices/device-association.service";
import {RefreshService} from "src/app/infrastructure/init/refresh/refresh.service";

@Injectable({providedIn: 'root'})
export class BootstrapService {

  private readonly animalsStore = inject(AnimalsStore);
  private readonly deviceStore = inject(DeviceStore);
  private readonly deviceAssociationStore = inject(DeviceAssociationStore);
  private readonly farmStore = inject(FarmStore);
  private readonly fixedSamplePointsStore = inject(FixedSamplePointsStore);
  private readonly gatewayStore = inject(GatewayStore);
  private readonly gatewayStatusStore = inject(GatewayStatusStore);
  private readonly mapStore = inject(MapStore);
  private readonly proximitySampleStore = inject(ProximitySampleStore);
  private readonly rssiStore = inject(RssiStore);
  private readonly userLocationStore = inject(UserLocationStore);
  private readonly waterPointsStore = inject(WaterPointsStore);

  constructor(
    private readonly aggregatedAssetStateService: AggregatedAssetStateService,
    private readonly aggregatedDeviceStateService: AggregatedDeviceStateService,
    private readonly animalsService: AnimalsService,
    private readonly assetSampleApiClient: AssetSampleApiClient,
    private readonly dataStorageService: DataStorageService,
    private readonly dataSubscriptionService: DataSubscriptionService,
    private readonly deviceAssociationService: DeviceAssociationService,
    private readonly deviceService: DeviceService,
    private readonly farmApiClient: FarmApiClient,
    private readonly farmService: FarmService,
    private readonly fixedSamplePointsService: FixedSamplePointsService,
    private readonly gatewayService: GatewayService,
    private readonly notificationsService: NotificationsService,
    private readonly paddockService: PaddockService,
    private readonly refreshService: RefreshService,
    private readonly rssiService: RssiService,
    private readonly waterLevelService: WaterLevelService,
    private readonly waterPointsService: WaterPointsService
  ) {
    this.userLocationStore.init();

    effect(() => {
      const animals = this.animalsStore.entities();
      const assetIds = animals.map(animal => animal.id);
      if (assetIds.length === 0) return;

      this.assetSampleApiClient.getAssetSamplesSince<Samples.ProximitySample>('Proximity', assetIds, sinceOneHour()).subscribe(samples => {
        this.proximitySampleStore.init(samples);
      });
    });

    effect(() => {
      const farm = this.farmStore.farm();
      if (!farm || this.fixedSamplePointsStore.loading()) return;

      const farmId = farm.id;
      const precipitationPoints = this.fixedSamplePointsStore.precipitationSamplePoints();
      this.loadPrecipitationSamplePointSamples(farmId, precipitationPoints);
    });

    effect(() => {
      const farm = this.farmStore.farm();
      if (!farm || this.fixedSamplePointsStore.loading()) return;

      const farmId = farm.id;
      const proximitySamplePoints = this.fixedSamplePointsStore.proximitySamplePoints();
      this.loadRssiForProximitySamplePoints(farmId, proximitySamplePoints);
    });

    effect(() => {
      const farm = this.farmStore.farm();
      if (!farm || this.deviceStore.loading()) return;

      const farmId = farm.id;
      const deviceIds = this.deviceStore.entities().map(device => device.id);
      this.aggregatedDeviceStateService.getAggregatedDeviceStates(farmId, deviceIds);
    });

    effect(() => {
      const farm = this.farmStore.farm();
      if (!farm || this.deviceAssociationStore.loading()) return;

      const farmId = farm.id;
      const deviceAssociations = this.deviceAssociationStore.entities();
      this.animalsService.loadAnimals(farmId, deviceAssociations);
    });

    effect(() => {
      const farm = this.farmStore.farm();
      if (!farm || this.animalsStore.loading() || this.fixedSamplePointsStore.loading()) return;

      const farmId = farm.id;
      const assetIds = [];
      assetIds.push(...this.animalsStore.entities().map(animal => animal.id));
      assetIds.push(...this.fixedSamplePointsStore.entities().map(fixedSamplePoint => fixedSamplePoint.id));
      this.aggregatedAssetStateService.getAggregatedAssetStates(farmId, assetIds);
    });

    effect(() => {
      const farm = this.farmStore.farm();
      if (!farm) return;

      const farmId = farm.id;
      this.fixedSamplePointsService.loadFixedSamplePoints(farmId);
    });

    effect(() => this.reloadWaterPoints());

    effect(() => {
      const farm = this.farmStore.farm();
      if (!farm || this.waterPointsStore.loadingEntities()) return;

      const farmId = farm.id;
      this.loadWaterPointSamples(farmId, this.waterPointsStore.entities());
    });

    effect(() => {
      const farm = this.farmStore.farm();
      if (!farm || this.deviceStore.loadingDevices()) return;

      const farmId = farm.id;
      this.deviceAssociationService.loadDeviceAssociations(farmId, this.deviceStore.entities());
    });

    effect(() => {
      const farm = this.farmStore.farm();
      if (!farm || this.gatewayStore.loading()) return;

      const farmId = farm.id;
      this.loadGatewayStatuses(farmId, this.gatewayStore.entities());
    });
  }

  async init(farmReference?: FarmReference) {
    this.setupFarm(farmReference);
    this.mapStore.init();
    this.notificationsService.loadNotifications();
  }

  destroy() {
    this.dataSubscriptionService.unsubscribeAll()
  }

  refresh() {
    const farm = this.farmStore.farm();
    if (!farm) return;

    this.loadAssetsAndSamples(farm.id);
    this.refreshService.recordRefresh();
  }

  async populateFarmWithAssets(farmReference: FarmReference) {
    await this.dataStorageService.addKeyValuePair('lastSelectedFarmReference', farmReference);
    await this.dataStorageService.addKeyValuePair(`farmReferences-${farmReference.id}`, farmReference)

    const farmId = farmReference.id;

    this.farmService.getFarm(farmId);
    this.loadAssetsAndSamples(farmId);
    this.refreshService.recordRefresh();
  }

  private async loadWaterPointSamples(farmId: string, waterPoints: WaterPoint[]) {
    if (waterPoints.length === 0) {
      await this.dataStorageService.addKeyValuePair(`waterPointAssetSamples-${farmId}`, []);
      this.waterPointsStore.setAssetSamples(new Map());
      return;
    }

    const waterPointIds = waterPoints.map(waterPoint => waterPoint.id!);
    this.assetSampleApiClient.getLatestAssetSamples<number>('WaterLevel', waterPointIds).subscribe({
      next: async (assetSamples) => {
        const waterPointsMap = new Map<WaterPoint, Array<Samples.AssetSample<number>>>();
        for (const assetSample of assetSamples.sort(descendingAssetSamples())) {
          const deviceId = assetSample.sample.deviceId;
          const association = this.deviceAssociationStore.deviceIdToAssociation().get(deviceId);
          const waterLevel = this.waterLevelService.getLevelAsPercentageFor(assetSample, association!);
          const refinedAssetSample: Samples.AssetSample<number> = {
            ...assetSample,
            sample: {
              ...assetSample.sample,
              value: waterLevel,
            }
          }
          const waterPoint = waterPoints.find(waterPoint => waterPoint.id == assetSample.assetId)
          if (hasMatchingKey(waterPointsMap, wp => wp.id == assetSample.assetId)) {
            getValueByKeyMatching(waterPointsMap, wp => wp.id == assetSample.assetId)?.push(assetSample)
          } else {
            waterPointsMap.set(waterPoint!, [refinedAssetSample!]);
          }
        }
        await this.dataStorageService.addKeyValuePair(`waterPointAssetSamples-${farmId}`, toAssetSamplePairs(waterPointsMap))
        this.waterPointsStore.setAssetSamples(waterPointsMap);
      },
      error: async () => {
        const cachedSamples = await this.dataStorageService.getValueByKey<AssetSamplesPair<WaterPoint, Samples.AssetSample<number>>[]>(`waterPointAssetSamples-${farmId}`);
        if (!cachedSamples) return;
        const waterPointsMap = toMap(cachedSamples);
        this.waterPointsStore.setAssetSamples(waterPointsMap);
      }
    });
  }

  reloadFixedAssetSamplePoints() {
    const farm = this.farmStore.farm();
    if (!farm) return;

    const farmId = farm.id;
    this.fixedSamplePointsService.loadFixedSamplePoints(farmId);
  }

  reloadWaterPoints() {
      const farm = this.farmStore.farm();
      if (!farm || this.deviceAssociationStore.loading()) return;

      const farmId = farm.id;
      const deviceAssociations = this.deviceAssociationStore.entities();
      this.waterPointsService.loadWaterPoints(farmId, deviceAssociations);
  }

  async loadRssiForProximitySamplePoints(farmId: string, proximitySamplePoints: FixedSamplePoint[]) {
    if (proximitySamplePoints.length === 0) {
      await this.dataStorageService.addKeyValuePair(`rssiForProximitySamplePoints-${farmId}`, []);
      return;
    }

    const deviceIds = proximitySamplePoints
      .map(proximitySamplePoint => proximitySamplePoint.id)
      .flatMap(assetId => this.deviceAssociationStore.assetIdToDeviceIds().get(assetId))
      .filter(deviceId => deviceId !== undefined);

    this.rssiService.latestBatteryVoltages(deviceIds).subscribe({
      next: async batteryVoltages => {
        await this.dataStorageService.addKeyValuePair(`rssiForProximitySamplePoints-${farmId}`, batteryVoltages);
        this.rssiStore.setDeviceIdToRssiPerGateway(batteryVoltages);
      },
      error: async () => {
        const cachedBatteryVoltages = await this.dataStorageService.getValueByKey<BatteryVoltage[]>(`rssiForProximitySamplePoints-${farmId}`);
        if (!cachedBatteryVoltages) return;

        this.rssiStore.setDeviceIdToRssiPerGateway(cachedBatteryVoltages);
      }
    });
  }

  async loadPrecipitationSamplePointSamples(farmId: string, precipitationPoints: FixedSamplePoint[]) {
    if (precipitationPoints.length === 0) {
      await this.dataStorageService.addKeyValuePair(`precipitationPointAssetSamples-${farmId}`, []);
      this.dataSubscriptionService.publish('precipitationPointAssetSamples', new Map());
      return;
    }

    const samplePointIds = precipitationPoints.map(samplePoints => samplePoints.id!);
    this.assetSampleApiClient.getLatestAssetSamples("Precipitation", samplePointIds).subscribe({
      next: async assetSamples => {
        if (assetSamples) {
          const precipitationPointAssetSamples = new Map<FixedSamplePoint, Samples.AssetSample<any>[]>()
          precipitationPoints.forEach(samplePoint => {
            precipitationPointAssetSamples.set(samplePoint, assetSamples.sort(descendingAssetSamples()).filter(assetSample => assetSample.assetId == samplePoint.id))
          })
          await this.dataStorageService.addKeyValuePair(`precipitationPointAssetSamples-${farmId}`, toAssetSamplePairs(precipitationPointAssetSamples));
          this.dataSubscriptionService.publish('precipitationPointAssetSamples', precipitationPointAssetSamples)
        }
      },
      error: async () => {
        const cachedSamples = await this.dataStorageService.getValueByKey<AssetSamplesPair<FixedSamplePoint, Samples.AssetSample<any>>[]>(`precipitationPointAssetSamples-${farmId}`);
        if (!cachedSamples) return;

        this.dataSubscriptionService.publish("precipitationPointAssetSamples", toMap(cachedSamples))
      }
    });
  }

  private async setupFarm(farmReference?: FarmReference) {
    this.farmApiClient.getFarmReferences().subscribe({
      next: async farmReferences => {
        if (!farmReference) {
          farmReference = farmReferences.at(0)!
        }

        await this.dataStorageService.addKeyValuePair('farmReferences', farmReferences);

        this.dataSubscriptionService.publish('farmReferences', farmReferences);

        this.populateFarmWithAssets(farmReference);
      },
      error: async () => {
        const cachedFarmReferences = await this.dataStorageService.getValueByKey<FarmReference[]>('farmReferences');
        if (!cachedFarmReferences) return;

        this.dataSubscriptionService.publish('farmReferences', cachedFarmReferences);

        const cachedLastSelectedFarmReference = await this.dataStorageService.getValueByKey<FarmReference>('lastSelectedFarmReference');
        if (!cachedLastSelectedFarmReference) return;

        this.populateFarmWithAssets(cachedLastSelectedFarmReference);
      }
    });
  }

  private loadAssetsAndSamples(farmId: string) {
    this.paddockService.loadPaddocks(farmId);
    this.gatewayService.loadGateways(farmId);
    this.deviceService.loadDevices(farmId);
  }

  private async loadGatewayStatuses(farmId: string, gateways: LoRaGateway[]) {
    const cachedStatuses = await this.dataStorageService.getValueByKey<LoRaGatewayStatus[]>(`gatewayStatuses-${farmId}`);
    this.gatewayStatusStore.setAllEntities(cachedStatuses);

    const gatewayStatuses: LoRaGatewayStatus[] = [];

    const result$ = gateways.map(gateway =>
      this.gatewayService.getStatus(gateway.id).pipe(
        tap(status => {
          if (!status) return;
          this.gatewayStatusStore.setEntity(status);
          gatewayStatuses.push(status);
        })
      )
    );

    forkJoin(result$).subscribe(async _ => {
      await this.dataStorageService.addKeyValuePair(`gatewayStatuses-${farmId}`, gatewayStatuses);
    });
  }
}

function toAssetSamplePairs<A extends Asset, S extends Samples.AssetSample<any>>(map: Map<A, S[]>): AssetSamplesPair<A, S>[] {
  return Array.from(map.entries()).map(entry => {
    return {
      asset: entry[0],
      assetSamples: entry[1],
    }
  })
}

function toMap<A extends Asset, S extends Samples.AssetSample<any>>(assetSamplePairs?: AssetSamplesPair<A, S>[]): Map<A, S[]> {
  const map = new Map<A, S[]>();
  if (!assetSamplePairs) return map;

  assetSamplePairs.forEach(assetSamplePair => {
    map.set(assetSamplePair.asset, assetSamplePair.assetSamples);
  });

  return map;
}

export interface AssetSamplesPair<A extends Asset, S extends Samples.AssetSample<any>> {
  asset: A
  assetSamples: S[]
}
